import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import {
  faBank,
  faCheck,
  faExchange,
  faFlag,
  faWallet,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp, faTelegramPlane } from "@fortawesome/free-brands-svg-icons";
import { withAppHOC } from "../../hoc";
import { StyledContentContainer, StyledIconedText } from "../../components/styled";
import { MessageBox } from "../../components/FormElements";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { formatCurrency, getAuthData } from "../../utils";
import { Link } from "react-router-dom";
import { TableBuilder } from "../../components/TableElements";
import { Image } from "../../components/Image";
import moment from "moment";

// -------------------- Styled Components --------------------
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const DashboardBackground = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8fafc, #e2e8f0);
  padding: 20px;
`;

const StyledHeading = styled.h1`
  font-size: 24px;
  font-weight: normal;
  color: #0f172a;
  margin-bottom: 20px;
`;

const StyledStatsContainerOuter = styled.section`
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  @media (max-width: 600px) {
    display: block;
  }
`;

const StyledStatsContainer = styled.section`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  a {
    text-decoration: none;
  }
  @media (max-width: 600px) {
    display: block;
  }
`;

const StyledStatsSection = styled.section`
  width: 300px;
  height: 100px;
  background: #ffffff;
  border-radius: 12px;
  display: grid;
  grid-template-columns: max-content auto;
  border: 1px solid #e2e8ee;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  /* Top accent bar */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 6px;
    width: 100%;
    background: ${(props) => props.bg};
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

  /* Glossy sweep */
  &::after {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.2) 0%,
      rgba(255, 255, 255, 0) 60%
    );
    transform: rotate(-25deg);
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 35px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 600px) {
    width: 100%;
    margin-bottom: 15px;
  }
`;

const StyledStatsIcon = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 18px;

  svg {
    width: 22px;
    height: 22px;
    color: #ffffff;
    padding: 12px;
    border-radius: 8px;
    background: ${(props) => props.iconBg};
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

const StyledStatsCounter = styled.section`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledStatsHeading = styled.p`
  font-size: 13px;
  margin: 0;
  padding: 0 15px;
  color: #000000ff;
  text-transform: uppercase;
  font-weight: 600;
`;

const StyledStatsCount = styled.h3`
  font-size: 24px;
  margin: 6px 0 0;
  padding: 0 15px;
  color: #0f172a;
`;

const StyledFlexBoxesContainer = styled.section`
  display: flex;
  column-gap: 30px;
  flex-wrap: wrap;
  padding: 20px;
  @media (max-width: 600px) {
    display: block;
  }
`;

const StyledTableContainer = styled.section`
  margin-bottom: 20px;
  flex: 1;
`;

const StyledTableContainerIn = styled.section`
  overflow: hidden;

  table {
    width: 100%;
    border-collapse: collapse;
    color: #0f172a;
  }

  thead {
    background: #4b5563;
    color: #ffffff;
  }

  tbody tr:nth-child(even) {
    background: #ffffff;
  }

  tbody tr:nth-child(odd) {
    background: #f9fafb;
  }

  td,
  th {
    padding: 10px 15px;
    text-align: left;
    vertical-align: middle;
  }

  tfoot {
    background: #374151;
    font-weight: bold;
    color: #ffffff;
  }

  tfoot td {
    padding: 10px 15px;
    text-align: left;
    vertical-align: middle;
  }
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;



// -------------------- MAIN DASHBOARD --------------------
const Dashboard = (props) => {
  const { userProfile } = getAuthData();
  const { isGSTSeller } = userProfile;
  const navigate = useNavigate();

  const [isQRVisible, setQRVisible] = useState(false);
  const [qrImage, setQRImage] = useState("/telegrambot.jpeg");
  const [supportNumber1, setSupportNumber1] = useState("");
  const [supportNumber2, setSupportNumber2] = useState("");

  const fetchFirmDetails = async () => {
    const response = await API.get("/meta/firm-details");
    const data = response.data?.data;

    const supportNumbers = data?.supportNumber
      ?.split(",")
      .map((n) => n.trim())
      .filter(Boolean);

    return supportNumbers?.slice(0, 2);
  };

  // 🔹 NEW: Force user to update email if missing
  useEffect(() => {
    // Defensive checks
    const email = userProfile?.email?.trim();

    if (!email) {
      // Redirect to profile page if email is empty
      navigate("/profile", {
        replace: true,
        state: {
          reason: "EMAIL_REQUIRED",
          message: "Please update your email address to continue"
        }
      });
    }
  }, [userProfile, navigate]);

  useEffect(() => {
    const loadSupport = async () => {
      const [num1, num2] = await fetchFirmDetails();
      setSupportNumber1(num1 || "");
      setSupportNumber2(num2 || "");
    };
    loadSupport();
  }, []);

  const fetchSummary = async (type) => {
    const response = await API.get(
      `/reports/operator-daybook?reportLocation=dashboard&reportType=${type}&marginType=${isGSTSeller === "Yes" ? "GST" : "Non-GST"
      }&fromDate=${moment().format("YYYY-MM-DD")}&toDate=${moment().format("YYYY-MM-DD")}`
    );
    return response.data;
  };

  const { data: saleSummary, isLoading: saleIsLoading } = useQuery({
    queryKey: ["sale_summary"],
    queryFn: () => fetchSummary("Seller"),
  });

  const { data: purchaseSummary, isLoading: purchaseIsLoading } = useQuery({
    queryKey: ["purchase_summary"],
    queryFn: () => fetchSummary("Buyer"),
  });

  const { data: dashboardData, error } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const res = await API.get("/dashboard");
      return res?.data?.data;
    },
  });

  if (error) {
    return (
      <StyledContentContainer>
        <MessageBox status={RESPONSE_STATUSES.FAILED} message={error.message} />
      </StyledContentContainer>
    );
  }

  const stats = dashboardData?.[0] || {};
  const { appData } = props;

  const cardThemes = [
    { top: "linear-gradient(90deg,#FFD700,#FFC700)", icon: "linear-gradient(135deg,#FFD700,#FFC700)" }, // gold
    { top: "linear-gradient(90deg,#FF416C,#FF4B2B)", icon: "linear-gradient(135deg,#FF416C,#FF4B2B)" },
    { top: "linear-gradient(90deg,#36D1DC,#5B86E5)", icon: "linear-gradient(135deg,#36D1DC,#5B86E5)" },
    { top: "linear-gradient(90deg,#8E2DE2,#4A00E0)", icon: "linear-gradient(135deg,#8E2DE2,#4A00E0)" },
  ];

  const truncate91 = (text = "") => {
    const s = String(text || "").trim();
    if (!s) return "";
    let digits = s.replace(/\D/g, "");
    if (digits.length > 10 && digits.startsWith("91")) digits = digits.replace(/^91/, "");
    if (digits.length > 10) digits = digits.slice(-10);
    return digits;
  };

  const renderCard = (icon, count, heading, link, index, qrSrc) => {
    const theme = cardThemes[index % cardThemes.length];

    const card = (
      <StyledStatsSection bg={theme.top}>
        <StyledStatsIcon iconBg={theme.icon}>
          <FontAwesomeIcon icon={icon} />
        </StyledStatsIcon>
        <StyledStatsCounter>
          <StyledStatsCount>{qrSrc ? "Scan QR" : count || 0}</StyledStatsCount>
          <StyledStatsHeading>{heading}</StyledStatsHeading>
        </StyledStatsCounter>
      </StyledStatsSection>
    );

    if (qrSrc) {
      return (
        <div key={index} onClick={() => { setQRImage(qrSrc); setQRVisible(true); }}>
          {card}
        </div>
      );
    }

    return (
      <Link to={link} key={index}>
        {card}
      </Link>
    );
  };

  return (
    <DashboardBackground>
      {stats && (
        <>
          <StyledHeading>Support</StyledHeading>
          <StyledStatsContainerOuter>
            <StyledStatsContainer>
              {renderCard(faTelegramPlane, null, "Telegram Bot", null, 99, "/telegrambot.jpeg")}
              {renderCard(faWhatsapp, truncate91(supportNumber1), "Support Number 1", `https://wa.me/91${truncate91(supportNumber1)}`, 1)}
              {supportNumber2 && renderCard(faWhatsapp, truncate91(supportNumber2), "Support Number 2", `https://wa.me/91${truncate91(supportNumber2)}`, 2)}
            </StyledStatsContainer>
          </StyledStatsContainerOuter>

          <StyledHeading>Sales Summary</StyledHeading>
          <StyledStatsContainerOuter>
            <StyledStatsContainer>
              {renderCard(faCheck, stats.salesSuccessRecharges, "Success Recharges", "/seller/transactions", 0)}
              {renderCard(faXmark, stats.salesFailedRecharges, "Failed Recharges", "/seller/transactions", 1)}
              {renderCard(faExchange, stats.salesPendingRecharges, "Pending Recharges", "/seller/transactions-pending", 2)}
              {renderCard(faFlag, stats.salesPendingComplaints, "Complaint Recharges", "/seller/transactions-complaints", 3)}
              {renderCard(faBank, stats.todaysWalletRedeem, "Withdrawal Amount", "/payments/withdraw-funds", 1)}
            </StyledStatsContainer>
          </StyledStatsContainerOuter>

          <StyledHeading>Purchase Summary</StyledHeading>
          <StyledStatsContainerOuter>
            <StyledStatsContainer>
              {renderCard(faCheck, stats.purchaseSuccessRecharges, "Success Recharges", "/buyer/transactions", 0)}
              {renderCard(faXmark, stats.purchaseFailedRecharges, "Failed Recharges", "/buyer/transactions", 1)}
              {renderCard(faExchange, stats.purchasePendingRecharges, "Pending Recharges", "/buyer/transactions-pending", 2)}
              {renderCard(faFlag, stats.purchasePendingComplaints, "Complaint Recharges", "/buyer/transactions-complaints", 3)}
              {renderCard(faWallet, stats.todaysWalletTopup, "Wallet Topup Amount", "/payments/wallet-topups", 0)}
            </StyledStatsContainer>
          </StyledStatsContainerOuter>

          <StyledFlexBoxesContainer>
            <StyledTableContainer>
              <StyledHeading>Sale Summary</StyledHeading>
              <StyledTableContainerIn>
                <TableBuilder
                  hasPagination={false}
                  isLoading={saleIsLoading}
                  tableHeadings={[
                    {
                      title: "Operator",
                      dataSelector: "operatorName",
                      cellrenderer: (val, item) => (
                        <StyledIconedText>
                          <Image source={item.operatorsLogo || "/no-image.png"} style={{ width: 50, height: 50 }} />
                          {val}
                        </StyledIconedText>
                      ),
                    },
                    {
                      title: "Amount",
                      dataSelector: "successAmount",
                      cellrenderer: (val, item) => `${formatCurrency(val)} (${item.successCount})`,
                    },
                    {
                      title: "Commission",
                      dataSelector: "commission",
                      cellrenderer: (val) => formatCurrency(val),
                    },
                    { title: "Avg Margin", dataSelector: "averageMargin" },
                  ]}
                  tableData={saleSummary?.data}
                />
              </StyledTableContainerIn>
            </StyledTableContainer>

            <StyledTableContainer>
              <StyledHeading>Purchase Summary</StyledHeading>
              <StyledTableContainerIn>
                <TableBuilder
                  hasPagination={false}
                  isLoading={purchaseIsLoading}
                  tableHeadings={[
                    {
                      title: "Operator",
                      dataSelector: "operatorName",
                      cellrenderer: (val, item) => (
                        <StyledIconedText>
                          <Image source={item.operatorsLogo || "/no-image.png"} style={{ width: 50, height: 50 }} />
                          {val}
                        </StyledIconedText>
                      ),
                    },
                    {
                      title: "Amount",
                      dataSelector: "successAmount",
                      cellrenderer: (val, item) => `${formatCurrency(val)} (${item.successCount})`,
                    },
                    {
                      title: "Commission",
                      dataSelector: "commission",
                      cellrenderer: (val) => formatCurrency(val),
                    },
                    { title: "Avg Margin", dataSelector: "averageMargin" },
                  ]}
                  tableData={purchaseSummary?.data}
                />
              </StyledTableContainerIn>
            </StyledTableContainer>
          </StyledFlexBoxesContainer>
        </>
      )}

      {isQRVisible && (
        <ModalOverlay onClick={() => setQRVisible(false)}>
          <ModalContent>
            <h3>Scan Telegram Bot QR</h3>
            <Image source={qrImage} style={{ width: "100%", maxWidth: 300 }} />
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardBackground>
  );
};

export default withAppHOC(Dashboard);
